import React, { useState } from "react";
import { KEYS, LABEL, unitFor, display, convert, ORIGIN_LABEL } from "./units";

// Shared editor used by the child card's Measurements tab AND the sizing dialog (one editor, one data store).
// draft: { [key]: { key, value, unit, origin, dirty } } — value/unit are what the parent typed (source of truth).
export function emptyDraftFromProfile(profile = []) {
  const d = {};
  for (const m of profile) d[m.key] = { key: m.key, value: m.value, unit: m.unit, origin: m.origin || "measured", dirty: false };
  return d;
}

// Module-level so the <input> keeps its identity (and focus) across parent re-renders.
function Field({ k, draft, system, idPrefix, onChange }) {
  const e = draft[k];
  const unit = unitFor(k, system);
  const shown = e ? (e.unit === unit ? e.value : display(e, system)) : "";
  return (
    <label className="block min-w-0">
      <span className="text-xs font-semibold text-ink-soft">{LABEL[k]}</span>
      <span className="mt-1 flex items-stretch rounded-xl border-2 border-line bg-white focus-within:border-brand overflow-hidden">
        <input data-testid={`${idPrefix}-${k}`} type="number" inputMode="decimal" step="0.1" min="0" aria-label={`${LABEL[k]} in ${unit}`} value={shown ?? ""} onChange={(ev) => onChange(k, ev.target.value)} placeholder="—" className="min-w-0 flex-1 h-11 px-3 text-base text-ink outline-none bg-transparent" />
        <span className="px-3 flex items-center text-sm font-bold text-ink-muted bg-cream border-l border-line" aria-hidden="true">{unit}</span>
      </span>
      {e && !e.dirty && e.origin && <span className="text-[11px] text-ink-muted" data-testid={`${idPrefix}-${k}-origin`}>{ORIGIN_LABEL[e.origin] || e.origin}</span>}
    </label>
  );
}

export function MeasurementsEditor({ draft, setDraft, system, setSystem, legacy = [], onConfirmLegacy, idPrefix = "me" }) {
  const [more, setMore] = useState(Object.keys(draft).some((k) => !["height", "weight"].includes(k) && draft[k]?.value !== ""));
  const [origin, setOrigin] = useState("measured");
  const setVal = (k, raw) => {
    const unit = unitFor(k, system);
    setDraft({ ...draft, [k]: { key: k, value: raw, unit, origin, dirty: true } });
  };
  return (
    <div className="space-y-3" data-testid={`${idPrefix}-editor`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div role="radiogroup" aria-label="Units" className="inline-flex rounded-full border-2 border-line bg-white p-0.5">
          {[["imperial", "in / lb"], ["metric", "cm / kg"]].map(([v, l]) => (
            <button key={v} type="button" role="radio" aria-checked={system === v} data-testid={`${idPrefix}-units-${v}`} onClick={() => setSystem(v)} className={`h-9 px-3 rounded-full text-xs font-bold transition-colors ${system === v ? "bg-brand text-white" : "text-ink-soft hover:bg-cream"}`}>{system === v ? "✓ " : ""}{l}</button>
          ))}
        </div>
        <div role="radiogroup" aria-label="How were new values obtained" className="inline-flex rounded-full border-2 border-line bg-white p-0.5">
          {[["measured", "Measured"], ["parent_estimate", "Estimate"]].map(([v, l]) => (
            <button key={v} type="button" role="radio" aria-checked={origin === v} data-testid={`${idPrefix}-origin-${v}`} onClick={() => setOrigin(v)} className={`h-9 px-3 rounded-full text-xs font-bold transition-colors ${origin === v ? "bg-ink text-white" : "text-ink-soft hover:bg-cream"}`}>{origin === v ? "✓ " : ""}{l}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field k="height" draft={draft} system={system} idPrefix={idPrefix} onChange={setVal} />
        <Field k="weight" draft={draft} system={system} idPrefix={idPrefix} onChange={setVal} />
      </div>
      {legacy.map((u) => (
        <div key={u.key} className="rounded-xl border border-gold/40 bg-gold/10 p-2.5 text-xs flex flex-wrap items-center gap-2" data-testid={`${idPrefix}-legacy-${u.key}`}>
          <span className="text-ink">Earlier entry: <b>{LABEL[u.key]} {u.value} {u.unit}</b>. Was it measured?</span>
          <button type="button" onClick={() => onConfirmLegacy(u.key, "measured")} data-testid={`${idPrefix}-legacy-${u.key}-measured`} className="h-9 px-3 rounded-full bg-ink text-white font-bold">Yes, measured</button>
          <button type="button" onClick={() => onConfirmLegacy(u.key, "parent_estimate")} data-testid={`${idPrefix}-legacy-${u.key}-estimate`} className="h-9 px-3 rounded-full border-2 border-line font-bold text-ink-soft">No, an estimate</button>
        </div>
      ))}
      <details className="text-xs text-ink-soft" data-testid={`${idPrefix}-howto`}>
        <summary className="cursor-pointer font-semibold text-brand min-h-[44px] flex items-center">How to measure</summary>
        <ul className="list-disc pl-4 mt-1 space-y-0.5">
          <li><b>Height:</b> shoes off, heels to a wall, floor to top of head.</li>
          <li><b>Chest:</b> tape around the fullest part under the arms, snug not tight.</li>
          <li><b>Waist:</b> around the natural waistline. <b>Hip:</b> fullest part of the seat.</li>
          <li><b>Inseam:</b> inside leg, crotch to ankle bone. <b>Foot length:</b> heel to longest toe, standing.</li>
        </ul>
      </details>
      <button type="button" data-testid={`${idPrefix}-more-toggle`} aria-expanded={more} onClick={() => setMore(!more)} className="min-h-[44px] text-sm font-bold text-brand hover:text-brand-hover">{more ? "Fewer measurements" : "More measurements"}</button>
      {more && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`${idPrefix}-more`}>
          {KEYS.filter((k) => !["height", "weight"].includes(k)).map((k) => <Field key={k} k={k} draft={draft} system={system} idPrefix={idPrefix} onChange={setVal} />)}
        </div>
      )}
    </div>
  );
}

// Entries to persist: only dirty, non-empty values, in the unit they were typed in.
export function changedMeasurements(draft) {
  return Object.values(draft).filter((e) => e.dirty && String(e.value).trim() !== "" && Number(e.value) > 0).map((e) => ({ key: e.key, value: Number(e.value), unit: e.unit, origin: e.origin || "measured" }));
}

export { convert };
